import React from 'react';
import type { EvaluationJobPreviewData } from '../../types/evaluationJob';
import { EVALUATION_MODE_LABELS, type EvaluationMode } from '../../types/evaluation';
import { formatPredictedCostUsd, pricingModelTooltip } from '../../utils/previewCost';
import './EvaluationPreviewResultView.css';

function formatNumber(value: number | null | undefined, fractionDigits = 0): string {
  if (value == null || Number.isNaN(value)) return '—';
  if (fractionDigits > 0) {
    return value.toLocaleString(undefined, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
  }
  return value.toLocaleString();
}

function formatEvaluationMode(mode: string | undefined): string {
  if (!mode) return '—';
  if (mode in EVALUATION_MODE_LABELS) {
    return EVALUATION_MODE_LABELS[mode as EvaluationMode];
  }
  return mode;
}

function MetricCard({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="preview-result-card">
      <div className="preview-result-card-label">{label}</div>
      <div className="preview-result-card-value">{value}</div>
      {hint ? <div className="evaluation-preview-result__hint">{hint}</div> : null}
    </div>
  );
}

export default function EvaluationPreviewResultView({ data }: { data: EvaluationJobPreviewData }) {
  const warnings = Array.isArray(data.warnings)
    ? data.warnings.filter((w): w is string => typeof w === 'string' && w.trim().length > 0)
    : [];
  const pricingTooltip = pricingModelTooltip(data.pricing_model_name);
  const costKnown = data.predicted_total_cost_usd != null;

  return (
    <div className="evaluation-preview-result optimization-preview-result">
      <div className="preview-result-cards">
        <MetricCard
          label="Predicted cost"
          value={
            costKnown ? (
              <span title={pricingTooltip} className="preview-result-card-value--cost">
                {formatPredictedCostUsd(data.predicted_total_cost_usd)}
              </span>
            ) : (
              <span title="Pricing unavailable for selected models">Cannot estimate</span>
            )
          }
          hint={
            costKnown && data.pricing_model_name
              ? `Priced at ${data.pricing_model_name}`
              : costKnown
                ? undefined
                : 'Not free — cost could not be priced'
          }
        />
        <MetricCard label="Expected input tokens" value={formatNumber(data.expected_input_tokens)} />
        <MetricCard label="Expected output tokens" value={formatNumber(data.expected_output_tokens)} />
        <MetricCard label="Expected total tokens" value={formatNumber(data.expected_total_tokens)} />
        <MetricCard
          label="Scorer worker tokens"
          value={formatNumber(data.expected_worker_tokens)}
          hint="Not included in API cost"
        />
        <MetricCard label="Evaluable dialogs" value={formatNumber(data.n_dialogs)} />
        <MetricCard label="Candidates" value={formatNumber(data.n_candidates)} />
        <MetricCard label="Calls per dialog" value={formatNumber(data.calls_per_dialog)} />
        <MetricCard label="Evaluation mode" value={formatEvaluationMode(data.evaluation_mode)} />
      </div>

      {data.evaluation_mode === 'config-scorer' ? (
        <div className="connector-message info evaluation-preview-result__note">
          <i className="fas fa-info-circle" aria-hidden />{' '}
          {data.artifact_ready
            ? 'Config scorer artifact is ready — training phase not included in this estimate.'
            : data.training_included
              ? 'Training phase is included in this estimate (artifact not ready yet).'
              : 'Config scorer preview — check artifact readiness before starting long runs.'}
        </div>
      ) : null}

      {warnings.length > 0 ? (
        <div className="evaluation-preview-result__notes" role="note">
          <div className="evaluation-preview-result__notes-title">Estimate notes</div>
          <ul className="evaluation-preview-result__notes-list">
            {warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
